import { useRef, useEffect, useMemo, useState } from "react";

import round from "lodash/round";

export interface ImageDimensions {
  width: number;
  height: number;
  src: string;
}

export const useImageHotspotHelper = (
  src: string,
  onClickHandler?: (ev: {
    x: number;
    y: number;
    height: number;
    width: number;
  }) => void
) => {
  const divRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [dimensions, setDimensions] = useState<ImageDimensions>();

  useEffect(() => {
    const img = document.createElement("img");

    img.onload = () => {
      setDimensions({
        width: img.width,
        height: img.height,
        src: src,
      });
    };

    img.src = src;
  }, [src]);

  function getRatios() {
    const wrapper = divRef.current;

    if (!dimensions || !wrapper) return { x: 1, y: 1, aspect: 1 };

    const aspectImg = dimensions.height / dimensions.width;
    const parentSlide = wrapper.closest("[data-slide]");
    const aspectWrapper =
      (wrapper.clientHeight || parentSlide?.clientHeight || 0) /
      (wrapper.clientWidth || parentSlide?.clientWidth || 0);

    return {
      x: Math.max(1, aspectWrapper / aspectImg),
      y: Math.max(1, aspectImg / aspectWrapper),
      aspect: dimensions.width / dimensions.height,
    };
  }

  const imageDisplay = useMemo(() => {
    const ratios = getRatios();

    const scaleX = round(ratios.x * 100, 2);
    const scaleY = round(ratios.y * 100, 2);

    const offsetX = round(((1 - ratios.x) / 2) * 100, 2);
    const offsetY = round(((1 - ratios.y) / 2) * 100, 2);

    return { ...dimensions, scaleX, scaleY, offsetX, offsetY };
  }, [dimensions]);

  function onClick(e: React.PointerEvent) {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.x) / rect.width) * 100;
    const y = ((e.clientY - rect.y) / rect.height) * 100;

    const width = (50 / rect.width) * 100;
    const height = (50 / rect.height) * 100;

    onClickHandler?.({ x, y, width, height });
  }

  return {
    onClick,
    imageDisplay,
    divRef,
    wrapperRef,
  };
};
